<script setup>
import { computed } from "vue";

import importedProps from "./config/rowProps";

const props = defineProps({ ...importedProps });

const rowClasses = computed(() => {
    const alignV = `align-items-${props.alignV}`;
    const alignH = `justify-content-${props.alignH}`;
    const cols = `row-cols-${props.cols}`;
    const colsXl = `row-cols-xl-${props.colsXl}`;
    const colsLg = `row-cols-lg-${props.colsLg}`;
    const colsMd = `row-cols-md-${props.colsMd}`;
    const colsSm = `row-cols-sm-${props.colsSm}`;

    const arrayClasses = ["row"];

    props.alignV && arrayClasses.push(alignV);
    props.alignH && arrayClasses.push(alignH);
    props.cols && arrayClasses.push(cols);
    props.colsXl && arrayClasses.push(colsXl);
    props.colsLg && arrayClasses.push(colsLg);
    props.colsMd && arrayClasses.push(colsMd);
    props.colsSm && arrayClasses.push(colsSm);

    return arrayClasses;
});
</script>

<template>
    <div :class="rowClasses">
        <slot />
    </div>
</template>

<style lang="scss" scoped>
.row {
    --row-gap-x: 10px;
    --row-gap-y: 15px;
    --row-percent-step-1: 100%;
    --row-percent-step-2: calc(100% / 2);
    --row-percent-step-3: calc(100% / 3);
    --row-percent-step-4: calc(100% / 4);
    --row-percent-step-5: calc(100% / 5);
    --row-percent-step-6: calc(100% / 6);

    display: flex;

    flex-wrap: wrap;

    row-gap: var(--row-gap-y);

    width: unset !important;
    margin-right: calc(-1 * var(--row-gap-x));
    margin-left: calc(-1 * var(--row-gap-x));

    &-cols-1 > * {
        flex: 0 0 var(--row-percent-step-1);

        max-width: var(--row-percent-step-1);
    }

    &-cols-2 > * {
        flex: 0 0 var(--row-percent-step-2);

        max-width: var(--row-percent-step-2);
    }

    &-cols-3 > * {
        flex: 0 0 var(--row-percent-step-3);

        max-width: var(--row-percent-step-3);
    }

    &-cols-4 > * {
        flex: 0 0 var(--row-percent-step-4);

        max-width: var(--row-percent-step-4);
    }

    &-cols-5 > * {
        flex: 0 0 var(--row-percent-step-5);

        max-width: var(--row-percent-step-5);
    }

    &-cols-6 > * {
        flex: 0 0 var(--row-percent-step-6);

        max-width: var(--row-percent-step-6);
    }
}

.align-items {
    &-start {
        align-items: flex-start !important;
    }

    &-center {
        align-items: center !important;
    }

    &-end {
        align-items: flex-end !important;
    }

    &-baseline {
        align-items: baseline !important;
    }

    &-stretch {
        align-items: stretch !important;
    }
}

.justify-content {
    &-start {
        justify-content: flex-start !important;
    }

    &-center {
        justify-content: center !important;
    }

    &-end {
        justify-content: flex-end !important;
    }

    &-evenly {
        justify-content: space-evenly !important;
    }

    &-around {
        justify-content: space-around !important;
    }

    &-between {
        justify-content: space-between !important;
    }
}

@media (min-width: 576px) {
    .row-cols-sm {
        &-1 > * {
            flex: 0 0 var(--row-percent-step-1);

            max-width: var(--row-percent-step-1);
        }

        &-2 > * {
            flex: 0 0 var(--row-percent-step-2);

            max-width: var(--row-percent-step-2);
        }

        &-3 > * {
            flex: 0 0 var(--row-percent-step-3);

            max-width: var(--row-percent-step-3);
        }

        &-4 > * {
            flex: 0 0 var(--row-percent-step-4);

            max-width: var(--row-percent-step-4);
        }

        &-5 > * {
            flex: 0 0 var(--row-percent-step-5);

            max-width: var(--row-percent-step-5);
        }

        &-6 > * {
            flex: 0 0 var(--row-percent-step-6);

            max-width: var(--row-percent-step-6);
        }
    }
}

@media (min-width: 768px) {
    .row-cols-md {
        &-1 > * {
            flex: 0 0 var(--row-percent-step-1);

            max-width: var(--row-percent-step-1);
        }

        &-2 > * {
            flex: 0 0 var(--row-percent-step-2);

            max-width: var(--row-percent-step-2);
        }

        &-3 > * {
            flex: 0 0 var(--row-percent-step-3);

            max-width: var(--row-percent-step-3);
        }

        &-4 > * {
            flex: 0 0 var(--row-percent-step-4);

            max-width: var(--row-percent-step-4);
        }

        &-5 > * {
            flex: 0 0 var(--row-percent-step-5);

            max-width: var(--row-percent-step-5);
        }

        &-6 > * {
            flex: 0 0 var(--row-percent-step-6);

            max-width: var(--row-percent-step-6);
        }
    }
}

@media (min-width: 992px) {
    .row-cols-lg {
        &-1 > * {
            flex: 0 0 var(--row-percent-step-1);

            max-width: var(--row-percent-step-1);
        }

        &-2 > * {
            flex: 0 0 var(--row-percent-step-2);

            max-width: var(--row-percent-step-2);
        }

        &-3 > * {
            flex: 0 0 var(--row-percent-step-3);

            max-width: var(--row-percent-step-3);
        }

        &-4 > * {
            flex: 0 0 var(--row-percent-step-4);

            max-width: var(--row-percent-step-4);
        }

        &-5 > * {
            flex: 0 0 var(--row-percent-step-5);

            max-width: var(--row-percent-step-5);
        }

        &-6 > * {
            flex: 0 0 var(--row-percent-step-6);

            max-width: var(--row-percent-step-6);
        }
    }
}

@media (min-width: 1200px) {
    .row-cols-xl {
        &-1 > * {
            flex: 0 0 var(--row-percent-step-1);

            max-width: var(--row-percent-step-1);
        }

        &-2 > * {
            flex: 0 0 var(--row-percent-step-2);

            max-width: var(--row-percent-step-2);
        }

        &-3 > * {
            flex: 0 0 var(--row-percent-step-3);

            max-width: var(--row-percent-step-3);
        }

        &-4 > * {
            flex: 0 0 var(--row-percent-step-4);

            max-width: var(--row-percent-step-4);
        }

        &-5 > * {
            flex: 0 0 var(--row-percent-step-5);

            max-width: var(--row-percent-step-5);
        }

        &-6 > * {
            flex: 0 0 var(--row-percent-step-6);

            max-width: var(--row-percent-step-6);
        }
    }
}
</style>
